/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {CVSAssetType} from '@ion/core/src/types/entities';
import Render from '@ion/dls/src/render/Render';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {useIsPhone} from '../../../hooks/useMedia';
import useIsHistoryAvailable from '../../../maps/hooks/useIsHistoryAvailable';
import {dlsColor} from '../../../theme/theme';
import {FindNearbyIconButton} from '../../buttons/FindNearbyIconButton';
import {MaintenanceIconButton} from '../../buttons/MaintenanceIconButton';
import {NavigateToIconButton} from '../../buttons/NavigateToIconButton';
import {StreetViewIconButton} from '../../buttons/StreetViewIconButton';
import {ViewHistoryIconButton} from '../../buttons/ViewHistoryIconButton';
import Tooltip from '../../tooltip/Tooltip';

export interface CommandBarProps {
  assetData: CVSAssetType;
  onFindNearby?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMaintenance?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onViewHistory?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  // opens the asset's street view in the app's Street View dialog - without
  // one, Google Street View opens in a new tab ...
  onStreetView?: (assetData: CVSAssetType) => void;
}

const useStyles = makeStyles(() => ({
  bar : {
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'row',
    flexWrap        : 'nowrap',
    alignItems      : 'center',
    gap             : '8px',
    width           : '100%',
    padding         : '16px',
    backgroundColor : dlsColor('grey6')
  },
  barPhone : {
    flexWrap : 'nowrap'
  },
  iconActionPhone : {
    flex     : '1 1 0',
    minWidth : 0,
    width    : 'auto'
  },
  iconAction : {
    boxSizing      : 'border-box',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    width          : '44px',
    height         : '44px',
    overflow       : 'hidden',
    '& > button'   : {
      boxSizing       : 'border-box',
      width           : '32px !important',
      height          : '32px !important',
      minWidth        : '32px !important',
      minHeight       : '32px !important',
      padding         : '4px !important',
      backgroundColor : `${dlsColor('white')} !important`,
      border          : `0.5px solid ${dlsColor('grey5')} !important`,
      borderRadius    : '8px !important'
    }
  }
}));

/**
 * CommandBar renders the Asset Detail Command Bar's row of 5 actions -
 * Street View, Navigate To, Find Nearby, Maintenance, and View History -
 * per the "Command Bar / Mixed" Figma variant, where the first 4 are
 * icon-only and the last renders wider with an icon+label pill.
 * `StreetViewIconButton`/`NavigateToIconButton` are used unmodified with
 * their existing `navigateToGoogleStreetView`/`navigateToGoogleDirections`
 * handlers; Find Nearby/Maintenance/View History have no reusable 1:1
 * handler in DLS (their real logic lives at the route level in
 * `AssetDetailsAccordion.tsx`, outside this ticket), so their `onClick`s
 * are left for the caller to wire.
 *
 * ```typescript
 *
 *   import {CommandBar} from
 *     '@ion/dls/src/components/commandbar/current/CommandBar';
 *   ...
 *
 *   <CommandBar assetData={assetData}
 *     onFindNearby={onFindNearby}
 *     onMaintenance={onMaintenance}
 *     onViewHistory={onViewHistory}
 *     onStreetView={openStreetView} />
 *
 * ```
 *
 */
export const CommandBar = memo(({
  assetData,
  onFindNearby,
  onMaintenance,
  onViewHistory,
  onStreetView
}: CommandBarProps) => {
  const classes          = useStyles();
  const isPhone          = useIsPhone();
  const isHistoryEnabled = useIsHistoryAvailable(assetData);
  const iconActionClass  = classnames(classes.iconAction, {[classes.iconActionPhone]: isPhone});
  return (
    <div className={classnames(classes.bar, {[classes.barPhone]: isPhone})}>
      <Tooltip title={translate('common:streetView')} arrow>
        <span className={iconActionClass}>
          <StreetViewIconButton assetData={assetData} onStreetView={onStreetView} hideLabel />
        </span>
      </Tooltip>
      <Tooltip title={translate('common:navigateTo')} arrow>
        <span className={iconActionClass}>
          <NavigateToIconButton assetData={assetData} hideLabel />
        </span>
      </Tooltip>
      <Render.If condition={!isCurrentTheme()}>
        <Tooltip title={translate('tracking:buttons.findNearby')} arrow>
          <span className={iconActionClass}>
            <FindNearbyIconButton onClick={onFindNearby} hideLabel />
          </span>
        </Tooltip>
        <Tooltip title={translate('nav:side.maintenance')} arrow>
          <span className={iconActionClass}>
            <MaintenanceIconButton onClick={onMaintenance} hideLabel />
          </span>
        </Tooltip>
      </Render.If>
      {isPhone ? (
        <Tooltip title={translate('tracking:buttons.viewHistory')} arrow>
          <span className={iconActionClass}>
            <ViewHistoryIconButton onClick={onViewHistory}
              disabled={!isHistoryEnabled}
              hideLabel />
          </span>
        </Tooltip>
      ) : (
        <ViewHistoryIconButton onClick={onViewHistory} disabled={!isHistoryEnabled} />
      )}
    </div>
  );
});

export default CommandBar;
